import { Backdrop, Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Divider, Typography } from '@mui/material'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import { useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { DownloadUploadsComponent } from '../../../DownloadUploadsComponent'
import { useContent } from '../../../../logic'
import { Alert } from '@mui/material'
import { useGetNotification } from '../../../../logic/useApiQuery'
import { apiRoutes } from '../../../../logic/apiRoutes'
import { PortalNotification } from '../../../../types/portalTypes/PortalNotification'
import { useInitiatePayloadPayment, useUserCompleteTask } from '../../../../logic/useApiMutation'
import { useQueryClient } from '@tanstack/react-query'
import { useOptimisticUpdateNotificationAndOrderNotification } from '../../../../logic/optimisticUpdates'
import { openCheckout } from 'payload-react'
import { InitiatePayloadPaymentResponse } from '../../../../types/portalTypes/api/InitiatePayloadPayment'
import { NumericFormat } from 'react-number-format';
import { EarnestMoneyTaskPayloadDialog } from './EarnestMoneyTaskPayloadDialog'
import { EarnestMoneyPayloadError, EarnestMoneyPayloadErrorDialog } from './EarnestMoneyPayloadErrorDialog'
import { formatCurrency } from '../../../../utils/formatCurrency'

// Flat processing fee added on top of the earnest money amount
const EARNEST_MONEY_FEE = 20

export function EarnestMoneyTaskPayload({ orderKey, notificationId }: {
  orderKey: string, notificationId: string,
}) {
  const { search } = useLocation()
  const { data: notification } = useGetNotification()
  // const { data: notificationStatuses } = useGetNotificationStatusesV2(orderKey, notificationId, search)
  const queryClient = useQueryClient()
  const { closingId, id } = useParams()

  const [showPaidAnotherWayConfirm, setShowPaidAnotherWayConfirm] = useState(false)


  const { TaskText } = useContent()
  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()

  const paidAnotherWayMutation = useUserCompleteTask(orderKey, notificationId, search, {
    onMutate: () => {
      if (notification?.NotificationId) {
        optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId: notification.NotificationId, search, patch: { IsComplete: true } })
        queryClient.setQueryData(
          [apiRoutes.getNotificationStatusesV2(orderKey, notificationId, search)],
          (old: Partial<PortalNotification> | undefined) => ({ ...old, IsComplete: true })
        )
      }
      setShowPaidAnotherWayConfirm(false)
    }
  })

  const [amount, setAmount] = useState<string | number>(0)
  const [checkClientToken, setCheckClientToken] = useState<string | null>(null)
  const [payloadError, setPayloadError] = useState<EarnestMoneyPayloadError | null>(null)
  const total = (Number(amount) || 0) + EARNEST_MONEY_FEE

  const { mutate: mutateInitiatePayloadPayment, isPending: initiatePayloadPaymentPending } = useInitiatePayloadPayment(closingId ?? '', id ?? '', search ?? '', {
    useErrorBoundary: true,
    onSuccess: (data: InitiatePayloadPaymentResponse, variables) => {
      if (!variables || !notification) return
      if (variables.type === 'bank_account') {
        openCheckout({
          clientToken: data.clientToken,
          onSuccess(evt) {
            optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId: notification.NotificationId, search, patch: { IsPaymentSubmitted: true } })
            console.log('Payment successful!', evt.transaction_id)
          },
          onDeclined(evt) {
            console.error('Payment failed:', evt)
            setPayloadError({ error: evt, source: 'bank_account' })
          },
          onClosed() {
            console.log('Customer closed checkout')
          }
        }).catch((error) => setPayloadError({ error, source: 'bank_account' }))
      } else if (variables.type === 'check') {
        setCheckClientToken(data.clientToken)
      }
    }
  })

  if (!notification || notification.IsCancelled) { //The portalTaskComponent handles the cancelled state, so we don't need to render anything here
    return <></>
  }

  const downloadUploadsComponent = (
    <DownloadUploadsComponent
      button={
        <Button
          variant="contained"
          sx={{
            borderRadius: "40px",
            minWidth: "250px",
            color: "white",
          }}
        >
          {`Download Receipt`}
        </Button>
      }
    />
  )

  if (notification.IsPaymentSubmitted && !notification.IsComplete) {
    return (
      <>
        <Alert severity="info" sx={{ mb: 3, width: '100%' }}>
          Your payment was submitted successfully. You may not see the withdrawal from your bank account right away, but it will post within 1-2 business days. We’ll send you a confirmation once it’s complete.
        </Alert>
        {downloadUploadsComponent}
      </>
    )
  }

  if (notification.IsComplete) {
    return (
      <>
        <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
          Thank you for completing your {TaskText}!
        </Alert>
        {notification.IsPaymentSubmitted ? downloadUploadsComponent : null}
      </>
    )
  }

  return (
    <>
      <Backdrop open={initiatePayloadPaymentPending} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box gap={3} display={'flex'} flexDirection={'column'}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>Amount</Typography>
          <NumericFormat
            allowNegative={false}
            allowLeadingZeros={false}
            value={amount}
            onValueChange={(values) => {
              setAmount(values.value)
            }}
            thousandSeparator={true}
            prefix="$"
            pl-input="amount"
            style={{
              width: '100%',
              minHeight: '40px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '8px',
              fontSize: '1rem',
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            }}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={0.5}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Fee</Typography>
            <Typography variant="body2" color="text.secondary">{formatCurrency(EARNEST_MONEY_FEE)}</Typography>
          </Box>
          <Divider />
          <Box display="flex" justifyContent="space-between">
            <Typography variant="subtitle2">Total</Typography>
            <Typography variant="subtitle2">{formatCurrency(total)}</Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={() => {
            mutateInitiatePayloadPayment({ amount: Number(amount) || 0, type: 'bank_account' })
          }}
        >
          Pay with Bank Account
        </Button>
        {/* <Button */}
        {/*   variant="contained" */}
        {/*   onClick={() => { */}
        {/*     mutateInitiatePayloadPayment({ amount: total, type: 'check' }) */}
        {/*   }} */}
        {/* > */}
        {/*   Pay with Check */}
        {/* </Button> */}
        <Button
          variant="outlined"
          onClick={() => setShowPaidAnotherWayConfirm(true)}
          startIcon={<SyncAltIcon />}
          sx={{ borderRadius: '40px', py: 1.2 }}
        >
          I Paid Another Way
        </Button>
      </Box>
      <Dialog open={showPaidAnotherWayConfirm} onClose={() => setShowPaidAnotherWayConfirm(false)}>
        <DialogTitle>I Paid Another Way</DialogTitle>
        <DialogContent>
          <DialogContentText>
            By confirming, you acknowledge that your earnest money deposit was submitted outside of this portal. This task will be marked as complete and no further action will be needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaidAnotherWayConfirm(false)} disabled={paidAnotherWayMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => paidAnotherWayMutation.mutate(undefined, {
              onSuccess: () => setShowPaidAnotherWayConfirm(false),
            })}
            disabled={paidAnotherWayMutation.isPending}
          >
            {paidAnotherWayMutation.isPending ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
      <EarnestMoneyTaskPayloadDialog
        clientToken={checkClientToken}
        onClose={() => setCheckClientToken(null)}
        onProcessed={() => {
          optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId: notification.NotificationId, search, patch: { IsPaymentSubmitted: true } })
          setCheckClientToken(null)
        }}
        onError={(error) => setPayloadError({ error, source: 'check' })}
      />
      {payloadError && (
        <EarnestMoneyPayloadErrorDialog payloadError={payloadError} onClose={() => setPayloadError(null)} />
      )}
    </>
  )
}
